import { ErrorCode, type FileError } from 'react-dropzone';

import type { FileUploaderProps } from 'src/components/FileUploader/FileUploader';
import { getHumanReadableStringOfAcceptedMimeTypes } from 'src/components/FileUploader/getHumanReadableStringOfAcceptedMimeTypes';
import { formatFileSize } from 'src/utils/formatFileSize';

/**
 * Returns the default error message to be displayed in an individual {@link FileListItem}
 * based on the provided `errorCode`.
 *
 * Returns `undefined` if the error shouldn't be displayed in individual {@link FileListItem}.
 *
 * @see getDefaultAlertErrorMessage
 *
 * @param options
 *
 * @access package
 */
export function getDefaultFileItemErrorMessage(
  options: DefaultErrorMessageFnOptions<'fileItem'>
): string | undefined {
  return getDefaultErrorMessageForDisplayLocation('fileItem', options);
}

/**
 * Returns the default error message to be displayed in the `Alert` rendered
 * below the {@link FileUploader}'s dropzone based on the provided `errorCode`.
 *
 * Returns `undefined` if the error shouldn't be displayed in the `Alert`.
 *
 * @see getDefaultFileItemErrorMessage
 *
 * @param options
 *
 * @access package
 */
export function getDefaultAlertErrorMessage(
  options: DefaultErrorMessageFnOptions<'topLevelAlert'>
): string | undefined {
  return getDefaultErrorMessageForDisplayLocation('topLevelAlert', options);
}

/**
 * Returns the default error message to be displayed in either the `Alert` rendered
 * below the {@link FileUploader}'s dropzone, or within an individual {@link FileListItem}
 * component based on the value of .
 *
 * Returns `undefined` if `options.displayLocation` does not match the default location
 * for the error message corresponding to the provided `options.errorCode`. The "default location"
 * of each error message is deterined by a number of factors, but mainly whether the `FileUploader`
 * component instance supports multiple file uploads.
 *
 * @see getDefaultFileItemErrorMessage
 * @see getDefaultAlertErrorMessage
 *
 * @param options
 *
 * @access package
 */
// FIXME (FED-4073) intl all these strings
export function getDefaultErrorMessageForDisplayLocation<
  Loc extends ErrorMessageDisplayLocations
>(
  displayLocation: Loc,
  options: DefaultErrorMessageFnOptions<Loc>
): string | undefined {
  const {
    errorCode,
    acceptedMimeTypes,
    maxNumberOfFiles,
    minSizeInBytes,
    maxSizeInBytes,
    fileValues = [],
  } = options;
  // By default, display as top level alert if only a single file is allowed
  let prescribedErrorDisplayLocation: ErrorMessageDisplayLocations =
    maxNumberOfFiles === 1 ? 'topLevelAlert' : 'fileItem';
  const excessFileCount = (fileValues?.length ?? 0) - maxNumberOfFiles;
  let messageByDisplayLocation: Record<
    ErrorMessageDisplayLocations,
    string | undefined
  >;

  const acceptedMimeTypesStr =
    getHumanReadableStringOfAcceptedMimeTypes(acceptedMimeTypes);

  switch (errorCode) {
    case ErrorCode.TooManyFiles:
      prescribedErrorDisplayLocation = 'topLevelAlert';
      messageByDisplayLocation = {
        topLevelAlert:
          maxNumberOfFiles === 1
            ? 'You can only upload 1 file.'
            : `You can upload up to ${maxNumberOfFiles} files.${
                fileValues
                  ? ` Please remove ${excessFileCount} fil${
                      excessFileCount === 1 ? 'e' : 'es'
                    }.`
                  : ''
              }`,
        fileItem: undefined,
      };

      break;
    case ErrorCode.FileInvalidType:
      messageByDisplayLocation = {
        topLevelAlert: `Acceptable file types are ${acceptedMimeTypesStr}.`,
        fileItem: `Not an accepted ${acceptedMimeTypesStr} file format.`,
      };
      break;
    case ErrorCode.FileTooLarge:
      messageByDisplayLocation = {
        topLevelAlert: `File exceeds ${
          maxSizeInBytes !== undefined ? formatFileSize(maxSizeInBytes) : ''
        } file size limit.`,
        fileItem: `File exceeds ${
          maxSizeInBytes !== undefined ? formatFileSize(maxSizeInBytes) : ''
        } file size limit.`,
      };
      break;
    // TODO (adl): Get this default message from Leah
    case ErrorCode.FileTooSmall:
      messageByDisplayLocation = {
        topLevelAlert: `File does not meet ${
          minSizeInBytes ? formatFileSize(minSizeInBytes) : ''
        } file size minimum.`,
        fileItem: `File does not meet ${
          minSizeInBytes ? formatFileSize(minSizeInBytes) : ''
        } file size minimum.`,
      };
      break;
    default:
      prescribedErrorDisplayLocation = 'topLevelAlert';
      messageByDisplayLocation = {
        // TODO: What should this be? Obviously an edge case... but...
        topLevelAlert: 'An unspecified error occurred. Please try again.',
        fileItem: undefined,
      };
      break;
  }

  if (displayLocation !== prescribedErrorDisplayLocation) {
    // If this function was called from a location within our componentry
    // that is not where the error should be displayed, return undefined.
    return undefined;
  }

  return messageByDisplayLocation[prescribedErrorDisplayLocation];
}

const possibleErrorMessageDisplayLocations = [
  'topLevelAlert',
  'fileItem',
] as const;
type ErrorMessageDisplayLocations =
  (typeof possibleErrorMessageDisplayLocations)[number];

type DefaultErrorMessageFnBaseOptions = {
  /**
   * The error code found in the {@link FileError} returned by the {@link FileUploaderDropzone}.
   */
  errorCode: FileError['code'];
  /**
   * The mime types accepted by the {@link FileUploader}.
   */
  acceptedMimeTypes: NonNullable<FileUploaderProps['acceptedMimeTypes']>;
  /**
   * The maximum number of files that can be uploaded.
   */
  maxNumberOfFiles: NonNullable<FileUploaderProps['maxNumberOfFiles']>;
  /**
   * The minimum file size.
   */
  minSizeInBytes?: FileUploaderProps['minSizeInBytes'];
  /**
   * The maximum file size.
   */
  maxSizeInBytes?: FileUploaderProps['maxSizeInBytes'];
};
type DefaultErrorMessageFnTopLevelAlertOptions =
  DefaultErrorMessageFnBaseOptions & {
    /**
     * The list of `FileModel`s used by the `FileUploader` component. Only used when `displayLocation` is 'topLevelAlert'.
     */
    fileValues: FileUploaderProps['fileValues'];
  };
type DefaultErrorMessageFnFileItemOptions = DefaultErrorMessageFnBaseOptions & {
  fileValues?: never;
};
type DefaultErrorMessageFnOptions<Loc extends ErrorMessageDisplayLocations> =
  Loc extends 'topLevelAlert'
    ? DefaultErrorMessageFnTopLevelAlertOptions
    : DefaultErrorMessageFnFileItemOptions;
